import { z } from 'zod'
import { currencySchema, minorAmountSchema } from './money.ts'

/**
 * The financial ledger — who owes whom.
 *
 * There is no payment gateway (D7) and there never will be in v1, but a mediator's
 * whole business is the spread and the settlement behind it, so the money has to be
 * tracked even though it moves by hand. This is real double entry, kept as small as
 * it can be: every posting is a set of legs that sum to zero, and a balance is a
 * sum over an account rather than a column somebody remembered to update.
 *
 * Deliberately NOT an accounting package. No periods, no closing, no journals to
 * reconcile — five accounts and a settlement form with two fields.
 */

export const LEDGER_ACCOUNTS = [
  'buyer_receivable',
  'seller_payable',
  'sales',
  'purchases',
  'cash',
] as const
export type LedgerAccount = (typeof LEDGER_ACCOUNTS)[number]
export const ledgerAccountSchema = z.enum(LEDGER_ACCOUNTS)

export const LEDGER_ACCOUNT_LABELS: Record<LedgerAccount, string> = {
  buyer_receivable: 'Customers owe us',
  seller_payable: 'We owe shops',
  sales: 'Sales',
  purchases: 'Cost of goods',
  cash: 'Cash',
}

/**
 * Which direction increases each account, so a balance can be summed without a
 * lookup table at every call site. Receivables, purchases and cash go up on a
 * debit; payables and sales go up on a credit.
 */
export const ACCOUNT_INCREASES_ON: Record<LedgerAccount, 'debit' | 'credit'> = {
  buyer_receivable: 'debit',
  seller_payable: 'credit',
  sales: 'credit',
  purchases: 'debit',
  cash: 'debit',
}

export const ledgerEntrySchema = z.object({
  id: z.string(),
  txnId: z.string(),
  account: ledgerAccountSchema,
  direction: z.enum(['debit', 'credit']),
  amountMinor: z.number().int(),
  currency: currencySchema,
  partyOrgId: z.string().nullable(),
  partyUserId: z.string().nullable(),
  partyName: z.string().nullable(),
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  refCode: z.string().nullable(),
  summary: z.string(),
  createdAt: z.number().int(),
})
export type LedgerEntry = z.infer<typeof ledgerEntrySchema>

/** One counterparty's outstanding position, for the list the desk actually reads. */
export const partyBalanceSchema = z.object({
  orgId: z.string().nullable(),
  userId: z.string().nullable(),
  name: z.string(),
  account: ledgerAccountSchema,
  /** Positive means outstanding in the account's natural direction. */
  balanceMinor: z.number().int(),
  lastMovedAt: z.number().int(),
})
export type PartyBalance = z.infer<typeof partyBalanceSchema>

export const moneySummarySchema = z.object({
  customersOweMinor: z.number().int(),
  weOweShopsMinor: z.number().int(),
  salesMinor: z.number().int(),
  purchasesMinor: z.number().int(),
  /** Sales minus cost of goods over the window. Not cash, and the screen says so. */
  marginMinor: z.number().int(),
  cashMinor: z.number().int(),
})
export type MoneySummary = z.infer<typeof moneySummarySchema>

export const SETTLEMENT_METHODS = ['cash', 'bank', 'cheque', 'adjustment'] as const
export type SettlementMethod = (typeof SETTLEMENT_METHODS)[number]

export const SETTLEMENT_METHOD_LABELS: Record<SettlementMethod, string> = {
  cash: 'Cash',
  bank: 'Bank transfer',
  cheque: 'Cheque',
  adjustment: 'Write-off or correction',
}

/**
 * Recording money that moved. Two fields that matter — who, and how much — which
 * is the whole point: anything longer does not get filled in at the counter.
 */
export const recordSettlementSchema = z
  .object({
    account: z.enum(['buyer_receivable', 'seller_payable']),
    orgId: z.string().max(64).nullish(),
    userId: z.string().max(64).nullish(),
    amountMinor: minorAmountSchema.refine((v) => v > 0, 'Enter an amount.'),
    method: z.enum(SETTLEMENT_METHODS).default('cash'),
    note: z.string().trim().max(300).nullish(),
  })
  .refine((v) => Boolean(v.orgId) !== Boolean(v.userId), {
    message: 'Pick exactly one customer or shop.',
    path: ['orgId'],
  })
export type RecordSettlementInput = z.infer<typeof recordSettlementSchema>
